import React, { useState, useEffect, useCallback } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
  PreviewCard,
} from "../../../components/Component";
import { useNavigate } from "react-router-dom";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { useForm, FormProvider } from "react-hook-form";
import { Col, Row, Alert } from "reactstrap";
import { Select } from "antd";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import Swal from "sweetalert2";
import superAdminService from "../../../api/super-admin/superAdminService";
import misc from "../../../api/misc/miscService";
import type {
  Country,
  State,
  AppDropdownItem,
} from "../../../types/super-admin";

const discountTypeOptions = [
  { value: "Percent", label: "Percent" },
  { value: "Flat Amount", label: "Flat Amount" },
];

// NOTE: App-wise discount scope is hidden for now (not in use)
// const discountScopeOptions = [
//   { value: "Overall Invoice", label: "Overall Invoice" },
//   { value: "App-wise", label: "App-wise" },
// ];

const usageTypeOptions = [
  { value: "Single-use", label: "Single-use" },
  { value: "Multi-use", label: "Multi-use" },
];

const CouponCreate: React.FC = () => {
  const navigate = useNavigate();
  const formMethods = useForm<Record<string, any>>({
    mode: "onChange",
    defaultValues: {
      discountType: "Percent",
      discountScope: "Overall Invoice",
      usageType: "Multi-use",
    },
  });
  const { handleSubmit, watch } = formMethods;

  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<{ value: any; label: string }[]>([]);
  const [states, setStates] = useState<{ value: any; label: string }[]>([]);
  const [appOptions, setAppOptions] = useState<{ value: number; label: string }[]>([]);
  const [selectedAppIds, setSelectedAppIds] = useState<number[]>([]);
  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [] as string[],
  });

  const selectedCountry = watch("countryId");
  const selectedDiscountType = watch("discountType");
  const selectedScope = watch("discountScope");
  const selectedUsageType = watch("usageType");
  const selectedValidFrom = watch("validFrom");

  const fetchDropdowns = useCallback(async () => {
    const results = await Promise.allSettled([
      misc.getCountries(),
      // NOTE: App dropdown hidden for now (App-wise scope not in use)
      // superAdminService.getAppDropdown(),
    ]);

    if (results[0].status === "fulfilled") {
      const countriesRes = results[0].value;
      if (countriesRes.status && countriesRes.data) {
        const list: Country[] = Array.isArray(countriesRes.data) ? countriesRes.data : [];
        setCountries(
          list.map((item) => ({ value: item.countryId, label: item.countryName }))
        );
      }
    }
  }, []);

  useEffect(() => {
    fetchDropdowns();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      const fetchStates = async () => {
        try {
          const statesRes = await misc.getStatesByCountry(Number(selectedCountry));
          if (statesRes.status && statesRes.data) {
            const list: State[] = Array.isArray(statesRes.data) ? statesRes.data : [];
            setStates(list.map((item) => ({ value: item.stateId, label: item.stateName })));
          }
        } catch (error: any) {
          console.error("Failed to fetch states:", error.message);
        }
      };
      fetchStates();
    } else {
      setStates([]);
    }
  }, [selectedCountry]);

  const submitForm = async (data: any) => {
    setLoading(true);
    setAlertInfo({ type: "", message: "", errors: [] });

    try {
      const payload: any = {
        couponCode: data.couponCode?.trim(),
        couponName: data.couponName?.trim(),
        discountType: data.discountType,
        discountScope: data.discountScope,
        usageType: data.usageType,
        validFrom: data.validFrom
          ? new Date(data.validFrom).toLocaleDateString("en-CA")
          : undefined,
        validTo: data.validTo
          ? new Date(data.validTo).toLocaleDateString("en-CA")
          : undefined,
      };

      if (data.description) payload.description = data.description.trim();
      if (data.discountType === "Percent") {
        payload.discountPercent = Number(data.discountPercent);
        payload.discountValue = 0;
      } else {
        payload.discountValue = Number(data.discountValue);
        payload.discountPercent = 0;
      }
      // NOTE: App-wise is hidden for now — always "Overall Invoice"
      // if (data.discountScope === "App-wise" && selectedAppIds.length > 0) {
      //   payload.applicableAppIds = selectedAppIds;
      // }
      if (data.usageType === "Multi-use" && data.maxRedemptions) {
        payload.maxRedemptions = Number(data.maxRedemptions);
      }
      if (data.countryId) payload.countryId = Number(data.countryId);
      if (data.stateId) payload.stateId = Number(data.stateId);

      const res = await superAdminService.createCoupon(payload);
      if (res.status) {
        Swal.fire("Created!", "Coupon has been created successfully.", "success");
        navigate("/sa-coupons");
      } else {
        const errorMsg = Array.isArray(res.message)
          ? res.message.join(", ")
          : res.message || "An error occurred";
        setAlertInfo({ type: "error", message: errorMsg, errors: [] });
      }
    } catch (error: any) {
      setAlertInfo({
        type: "error",
        message: error.message || "An error occurred",
        errors: [],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Head title="Create Coupon" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Create Coupon
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>Create a new discount coupon</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="primary"
                className="btn-dim"
                onClick={() => navigate("/sa-coupons")}
              >
                <Icon name="menu-alt-r"></Icon>
                <span>Coupon List</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <PreviewCard className="cardBox" bodyClass="card-inner-xl">
            {alertInfo.message && (
              <div className="mb-3">
                <Alert
                  color={alertInfo.type === "error" ? "danger" : "success"}
                  className="alert-icon alert-animated"
                >
                  <strong>{alertInfo.message}</strong>
                  <Icon
                    name={
                      alertInfo.type === "error"
                        ? "alert-circle"
                        : "check-circle"
                    }
                  />
                </Alert>
              </div>
            )}
            <FormProvider {...formMethods}>
              <form
                className="content clearfix"
                onSubmit={handleSubmit(submitForm)}
              >
                {/* Basic Information */}
                <h6 className="overline-title text-primary mb-3">Basic Information</h6>
                <Row className="gy-3">
                  <Col lg="4">
                    <FormInput
                      name="couponCode"
                      label="Coupon Code"
                      type="text"
                      required={true}
                      maxLength={50}
                      placeholder="e.g. SUMMER2026"
                    />
                  </Col>
                  <Col lg="4">
                    <FormInput
                      name="couponName"
                      label="Coupon Name"
                      type="text"
                      required={true}
                      maxLength={200}
                      placeholder="Enter coupon name"
                    />
                  </Col>
                  <Col lg="4">
                    <FormInput
                      name="description"
                      label="Description"
                      type="text"
                      maxLength={255}
                      placeholder="Optional description"
                    />
                  </Col>
                </Row>

                <hr className="my-4" />

                {/* Discount Settings */}
                <h6 className="overline-title text-primary mb-3">Discount Settings</h6>
                <Row className="gy-3">
                  <Col lg="4">
                    <FormSelect
                      name="discountType"
                      label="Discount Type"
                      options={discountTypeOptions}
                      required={true}
                      placeholder="-Select-"
                    />
                  </Col>
                  {selectedDiscountType === "Percent" && (
                    <Col lg="4">
                      <FormInput
                        name="discountPercent"
                        label="Discount %"
                        type="number"
                        required={true}
                        placeholder="1 - 100"
                        customValidation={{
                          validate: (val: any) => {
                            const num = Number(val);
                            if (num < 1 || num > 100) return "Must be between 1 and 100";
                            return true;
                          },
                        }}
                      />
                    </Col>
                  )}
                  {selectedDiscountType === "Flat Amount" && (
                    <Col lg="4">
                      <FormInput
                        name="discountValue"
                        label="Discount Amount"
                        type="number"
                        required={true}
                        placeholder="Enter amount"
                      />
                    </Col>
                  )}
                  {/* NOTE: Discount Scope (App-wise) is hidden for now — defaulting to "Overall Invoice"
                  <Col lg="4">
                    <FormSelect
                      name="discountScope"
                      label="Discount Scope"
                      options={discountScopeOptions}
                      required={true}
                      placeholder="-Select-"
                    />
                  </Col>
                  */}
                </Row>

                {/* NOTE: App-wise application selection is hidden for now
                {selectedScope === "App-wise" && (
                  <Row className="gy-3 mt-2">
                    <Col lg="12">
                      <div className="form-group">
                        <label className="form-label">Applicable Applications</label>
                        <Select
                          mode="multiple"
                          placeholder="Select applications this coupon applies to"
                          style={{ width: "100%" }}
                          showSearch
                          optionFilterProp="label"
                          value={selectedAppIds}
                          onChange={(val) => setSelectedAppIds(val)}
                          options={appOptions}
                        />
                      </div>
                    </Col>
                  </Row>
                )}
                */}

                <hr className="my-4" />

                {/* Usage & Location */}
                <h6 className="overline-title text-primary mb-3">Usage & Location</h6>
                <Row className="gy-3">
                  <Col lg="4">
                    <FormSelect
                      name="usageType"
                      label="Usage Type"
                      options={usageTypeOptions}
                      required={true}
                      placeholder="-Select-"
                    />
                  </Col>
                  {selectedUsageType === "Multi-use" && (
                    <Col lg="4">
                      <FormInput
                        name="maxRedemptions"
                        label="Max Redemptions"
                        type="number"
                        placeholder="Empty = unlimited"
                      />
                    </Col>
                  )}
                  <Col lg="4">
                    <FormSelect
                      name="countryId"
                      label="Country"
                      options={countries}
                      placeholder="Global if empty"
                      showSearch={true}
                    />
                  </Col>
                  {selectedCountry && (
                    <Col lg="4">
                      <FormSelect
                        name="stateId"
                        label="State"
                        options={states}
                        placeholder="-Select State-"
                        showSearch={true}
                      />
                    </Col>
                  )}
                </Row>

                <hr className="my-4" />

                {/* Validity Period */}
                <h6 className="overline-title text-primary mb-3">Validity Period</h6>
                <Row className="gy-3">
                  <Col lg="4">
                    <FormDatePicker
                      name="validFrom"
                      label="Valid From"
                      required={true}
                      placeholder="Start date"
                    />
                  </Col>
                  <Col lg="4">
                    <FormDatePicker
                      name="validTo"
                      label="Valid To"
                      required={true}
                      placeholder="End date"
                      disabled={!selectedValidFrom}
                      minDate={selectedValidFrom ? new Date(selectedValidFrom) : undefined}
                    />
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col lg="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                      <li>
                        <Button color="primary" size="md" type="submit">
                          {loading ? <LoadingSpinner size="sm" inline /> : "Create Coupon"}
                        </Button>
                      </li>
                      <li>
                        <a
                          href="#cancel"
                          onClick={(e) => {
                            e.preventDefault();
                            if (!loading) navigate("/sa-coupons");
                          }}
                          className={`link link-light${loading ? " disabled text-muted" : ""}`}
                          style={loading ? { pointerEvents: "none" } : {}}
                        >
                          Cancel
                        </a>
                      </li>
                    </ul>
                  </Col>
                </Row>
              </form>
            </FormProvider>
          </PreviewCard>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default CouponCreate;
